import { Circ, Expo, Linear, Sine, TimelineLite, TimelineMax, TweenLite, TweenMax } from "gsap";
import * as HLF from "html-living-framework";
import * as PIXI from "pixi.js";
import {Ease} from "../core/Animatable";
import {Container} from "../core/Container";
import {array} from "../core/Reflection";
import {Sprite} from "../core/Sprite";
import { Button, OutlinedButton } from "../gameobjects/Button";
import {AbstractGameState} from "./AbstractGameState";

// @ts-ignore
import {BezierPlugin} from "gsap/BezierPlugin";
import { PlayMode } from "html-living-framework";
import { random } from "html-living-framework/lib/Utils/ArrayUtils";
import { sendToFront } from "html-living-framework/lib/Utils/PixiUtils";
import { isMobile } from "html-living-framework/lib/Utils/PlatformUtils";
import { Emitter } from "pixi-particles";
import { Application } from "../Application";
import { texture } from "../core/AssetHelpers";
import { CaptionToken } from "../data/CaptionToken";
import LightsOutGameModel from "../data/LightsOutGameModel";
import * as PlanterGameModel from "../data/PlanterGameModel";
import TrashGameModel from "../data/TrashGameModel";
import * as VO from "../data/VO";
import { randomExhaust } from "../data/VO";
import { ColorOverlayFilter } from "../filters/ColorOverlayFilter";
import { Abby } from "../gameobjects/Abby";
import { CustomizedPlanter } from "../gameobjects/CustomizedPlanter";
import { CustomizeOptionButton } from "../gameobjects/CustomizeOptionButton";
import { CustomizeTab } from "../gameobjects/CustomizeTab";
import { PlantButton } from "../gameobjects/PlantButton";
import { Planter } from "../gameobjects/Planter";
import { PlanterPartyEasterEgg } from "../gameobjects/PlanterPartyEasterEgg";
import {PlanterPlantingLayout} from "../layout/PlanterPlantingLayout";
import { CaptionManager } from "../managers/CaptionManager";
import { PlantingBadgePopup } from "../popups/PlantingBadgePopup";
const dontTreeShake = [BezierPlugin];

const clickVO = isMobile() ? VO.PP_PROMPT01 : VO.PP_PROMPT02;
const idleVOs = randomExhaust([[clickVO, VO.PP_PLANTING_TO01], VO.PP_PLANTING_TO03]);
const abbyClickReactVO = [VO.PP_PLANTING07, VO.PP_PLANTING08];
const plantReactVO = randomExhaust([VO.PP_PLANTING03, VO.PP_PLANTING04, VO.PP_PLANTING05, VO.PP_PLANTING06]);
HLF.ArrayUtils.shuffle(abbyClickReactVO);

export class PlanterPlantingScreen extends AbstractGameState {

    public static readonly NAME = "PlanterPlantingScreen";

    public readonly bg = this.addChild(new Sprite());

    public readonly blueButterfly = this.addChild(new PlanterPartyEasterEgg({
        frames: 3,
        spriteSheet: ["planterplanting-1"],
        loopCount: -1,
    }));
    public readonly yellowButterfly = this.addChild(new PlanterPartyEasterEgg({
        frames: 3,
        spriteSheet: ["planterplanting-1"],
        loopCount: -1,
    }));
    public readonly purpleButterfly = this.addChild(new PlanterPartyEasterEgg({
        frames: 3,
        spriteSheet: ["planterplanting-1"],
        loopCount: -1,
    }));
    public readonly bee = this.addChild(new Button());
    public readonly planterContainer = this.addChild(new Container());
    public readonly planter = array(() => this.planterContainer.addChild(new Planter()));
    public readonly customizedPlanter = this.planterContainer.addChild(new CustomizedPlanter());
    public readonly anotherBee = this.addChild(new Button());

    public readonly windchimes = this.addChild(new PlanterPartyEasterEgg({
        frames: 5,
        spriteSheet: ["planterplanting-1"],
        loopCount: 2,
    }));
    public readonly hose = this.addChild(new PlanterPartyEasterEgg({
        frames: 2,
        spriteSheet: ["planterplanting-1"],
        loopCount: 9,
    }));
    public readonly shovel = this.addChild(new PlanterPartyEasterEgg({
        frames: 16,
        spriteSheet: ["planterplanting-1"],
    }));

    public readonly abby = this.addChild(new Abby());

    public readonly buttonsContainer = this.addChild(new Container());
    public readonly buttonsBg = this.buttonsContainer.addChild(new Sprite());
    public readonly button = [
        ["sunflower", VO.PP_PLANT02, VO.PP_SEED02],
        ["tomato", VO.PP_PLANT01, VO.PP_SEED01],
        ["tulip", VO.PP_PLANT03, VO.PP_SEED03],
        ["redflower", VO.PP_PLANT05, VO.PP_SEED05],
        ["pepper", VO.PP_PLANT04, VO.PP_SEED04],
        ["blueflower", VO.PP_PLANT06, VO.PP_SEED05],
    ].map((it) => this.buttonsContainer.addChild(new PlantButton(it[0], it[1], it[2])));

    public readonly continueButton = this.addChild(new Button("ArrowBTN.png"));
    public readonly homeButton = this.addChild(new OutlinedButton());

    protected analyticsScreenName: string = "6b Planter Party_Plant Screen";
    protected analyticsRoundName = "Planter Party";

    protected _subscriptions: any[] = [];

    protected selectedPlant?: PlantButton;
    protected selectedPlanter?: Planter;

    protected planterPreviousPosition: PIXI.IPointData = {x: 0, y: 0};
    protected planterHighlightedPosition: PIXI.IPointData = {x: 0, y: 0};

    protected clickCount: number = 0;
    protected screenStartTime: number = 0;

    protected pollinators = [this.bee, this.blueButterfly, this.purpleButterfly, this.yellowButterfly, this.anotherBee];

    protected wandParticlesContainer = this.addChild(new PIXI.Container());
    protected wandParticles = new Emitter(this.wandParticlesContainer, [
        texture("blue-circle"),
        texture("blue-star"),
        texture("pink-circle"),
        texture("pink-star"),
        texture("yellow-star"),
    ], {
        alpha: {
            start: 1,
            end: 0,
        },
        scale: {
            start: 2,
            end: 0.001,
            minimumScaleMultiplier: 0.5,
        },
        color: {
            start: "#ffffff",
            end: "#ffffff",
        },
        speed: {
            start: 100,
            end: 0,
            minimumSpeedMultiplier: 0.75,
        },
        acceleration: {
            x: 0,
            y: 0,
        },
        maxSpeed: 0,
        startRotation: {
            min: 0,
            max: 0,
        },
        noRotation: false,
        rotationSpeed: {
            min: 0,
            max: 0,
        },
        lifetime: {
            min: 1,
            max: 4,
        },
        blendMode: "normal",
        frequency: 0.05,
        emitterLifetime: -1,
        maxParticles: 1500,
        pos: {
            x: 0,
            y: 0,
        },
        addAtBack: true,
        spawnType: "ring",
        spawnCircle: {
            x: 0,
            y: 0,
            r: 30,
            minR: 30,
        },
    });

    protected hoverTimelines: TimelineMax[] = [];

    public init(pSize: PIXI.Point, pData: {}) {
        PlanterPlantingLayout.call(this);
        this.abby.scale.set(0.1);
        this.abby.y -= 15;
        this.shovel.scale.x = Math.abs(this.shovel.scale.x) * -1;
        this.homeButton.onClick.add(() => {
            this.trackEvent("gameMenuClick", {buttonName: "Home"});
            this.gotoNextState("MainMenu");
        });
        this.homeButton.clickSfx = "buttonclick";

        this._subscriptions.push(PubSub.subscribe("button clicked", this.onPlantSelected.bind(this)));
        this.customizedPlanter.scale.set(0.25);
        this.continueButton.visible = false;

        this.abby.interactive = true;
        this.abby.buttonMode = true;
        this.abby.accessible = true;
        this.abby.addListener(HLF.Input.Events.POINTER_TAP, () => {
            if (this.idleStartTime !== undefined) {
                const idleEndTime = new Date().valueOf();
                const idleDuration = (idleEndTime - this.idleStartTime) / 1000;
                this.trackEvent("gameTOReEngage", {duration: idleDuration});
                this.idleStartTime = undefined;
            }
            this.idleTimeline?.restart(true);
            const voToPlay = abbyClickReactVO[0];

            // cycle vo only if it actually played
            const onVoDidPlay = (_pTopic: string, _pData: string) => {
                this.abby.Smile();
                if (_pData === voToPlay) {
                    if (abbyClickReactVO[0] === voToPlay) {
                        abbyClickReactVO.push(abbyClickReactVO.shift()!);
                    }
                }
                PubSub.unsubscribe(token);
            };
            const token = PubSub.subscribe(HLF.VOICEOVER_STARTED, onVoDidPlay);

            Application.instance.voiceover.playVO(voToPlay!, HLF.PlayMode.New, () => {
                this.button.forEach((b) => b.stopPulse());
                this.idleTimeline?.restart(true);
                PubSub.unsubscribe(token);
            });
        });

        const easterEggs = [this.windchimes, this.hose, this.shovel];
        easterEggs.forEach((egg) => {
            egg.onClick.add(() => {
                if (this.idleStartTime !== undefined) {
                    const idleEndTime = new Date().valueOf();
                    const idleDuration = (idleEndTime - this.idleStartTime) / 1000;
                    this.trackEvent("gameTOReEngage", {duration: idleDuration});
                    this.idleStartTime = undefined;
                }
                this.trackEvent("gameInteraction", {objectName: egg.name.replace(/^\w/, (c) => c.toUpperCase())});
                egg.TurnOn();
                this.abby.Smile();
                this.idleTimeline?.restart(true);
            });
        });

        this.shovel.clickSfx = "planter-party-shovel-dirt";
        this.shovel.clickSfxCanOverlap = false;
        this.hose.clickSfx = "planter-party-hose";
        this.hose.clickSfxCanOverlap = false;
        this.windchimes.clickSfx = "planter-party-wood-chimes";
        this.windchimes.clickSfxCanOverlap = false;

        this.button.forEach((b) => b.interactive = false); // enable after planter tween complete

        this.pollinators.forEach((bug) => {
            bug.visible = false;
            bug.interactive = false;
            bug.buttonMode = false;
        });

        this.wandParticles.autoUpdate = true;
        this.wandParticles.emit = false;

        this.trackEvent("gameComponentStart", {
            componentType: "Simulation",
            componentName: "Plant",
        });
        this.screenStartTime = new Date().valueOf();
        super.init(pSize);
    }

    public animateIn(pOnComplete: () => void) {
        super.animateIn(pOnComplete);
        const bgm = HLF.Application.instance.audio.getAudioTrack("planter_party_loop", HLF.AudioCategory.BGM) ?? HLF.Application.instance.audio.createAudioTrack("planter_party_loop", HLF.AudioCategory.BGM);
        if (bgm.isPlaying() === false) {
            bgm.setLooped(true);
            bgm.play();
        }
        this.button.forEach((b) => b.pulse());
        Application.instance.voiceover.playVO(VO.PP_PLANTING01, {priority: 2, callback: () => {
            this.button.forEach((b) => b.stopPulse());
            this.idleTimeline.pause();
            this.idleTimeline.call(() => {
                this.trackEvent("gameTimeoutPlay");
                if (this.idleStartTime === undefined) {
                    this.idleStartTime = new Date().valueOf();
                }
                this.button.forEach((b) => b.pulse());

                if (this.selectedPlanter !== this.customizedPlanter) {

                    const idleVO = idleVOs();

                    if (idleVO instanceof Array) {
                        Application.instance.voiceover.playVO(idleVO, {
                            mode: PlayMode.New,
                            callback: () => {
                                this.button.forEach((b) => b.stopPulse());
                                this.idleTimeline?.restart(true);
                            },
                            skipCC: true,
                            priority: 0,
                        });
                        PubSub.publishSync(HLF.PLAY_CAPTION, new CaptionToken(idleVO[1], 0, {
                            prompt: clickVO,
                        }));
                    }
                    else {
                        Application.instance.voiceover.playVO(idleVO, {
                            mode: PlayMode.New,
                            callback: () => {
                                this.button.forEach((b) => b.stopPulse());
                                this.idleTimeline?.restart(true);
                            },
                            priority: 0,
                        });
                    }
                }
                else {
                    Application.instance.voiceover.playVO([clickVO, VO.PP_PLANTING_TO01], {
                        mode: PlayMode.New,
                        callback: () => {
                            this.button.forEach((b) => b.stopPulse());
                            this.idleTimeline?.restart(true);
                        },
                        skipCC: true,
                        priority: 0,
                    });
                    PubSub.publishSync(HLF.PLAY_CAPTION, new CaptionToken(VO.PP_PLANTING_TO01, 0, {
                        prompt: clickVO,
                    }));
                }
                this.idleTimeline?.restart(true);
            });
            this.idleTimeline.restart(true);
        }});

        this.planterPreviousPosition = this.customizedPlanter.position.clone();

        // TODO: put this in the layout .fla
        this.planterHighlightedPosition = this.customizedPlanter.position.clone();

        this.selectedPlanter = this.customizedPlanter;

        sendToFront(this.customizedPlanter);
        const customizedPlanterHighlightedScale = 1;
        new TimelineLite().to(this.customizedPlanter, 0.5, {pixi: {
            scale: customizedPlanterHighlightedScale,
            x: this.planterHighlightedPosition.x,
            y: this.planterHighlightedPosition.y,
        }})
        .call(() => {
            this.button.forEach((b) => b.interactive = true);
        })
        .delay(2);
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
        this.position.set((pSize.x - 1664) / 2, 0);
        this.homeButton.position.x = ((1664 - pSize.x) / 2) + 108;
        this.continueButton.position.set(1280, pSize.y - 100); // temp
    }

    public update(pDeltaTime: number) {
        super.update(pDeltaTime);
        this.abby.update(pDeltaTime);
    }

    public destroy() {
        super.destroy();
        for (const subscription of this._subscriptions) {
            PubSub.unsubscribe(subscription);
        }
        this.idleTimeline?.kill();
        this.idleTimeline?.clear();
        this.hoverTimelines.forEach((t) => {
            t.kill();
            t.clear();
        });
        this.wandParticles.autoUpdate = false;
        this.wandParticles.cleanup();
    }

    protected onBadgePopupClosed() {
        this.abby.visible = true;
    }

    protected onPlantSelected(pTopic: "option selected", pData: PlantButton) {
        if (this.idleStartTime !== undefined) {
            const idleEndTime = new Date().valueOf();
            const idleDuration = (idleEndTime - this.idleStartTime) / 1000;
            this.trackEvent("gameTOReEngage", {duration: idleDuration});
            this.idleStartTime = undefined;
        }
        this.selectedPlant = pData;
        this.button.forEach((b) => b.stopPulse());
        this.idleTimeline?.restart(true);

        if (this.selectedPlanter !== undefined) {
            this.clickCount++;
            this.Plant(this.selectedPlant, this.selectedPlanter);
            this.selectedPlanter = undefined;

            if (this.clickCount % 2 === 0) {
                Application.instance.voiceover.playVO(VO.PP_PLANTING02, HLF.PlayMode.Override, () => {
                    this.idleTimeline?.restart(true);
                    Application.instance.audio.play("wand-002-gentle", 0.5, false, HLF.AudioCategory.SFX);
                });
            }
            else {
                Application.instance.audio.play("wand-002-gentle", 0.5, false, HLF.AudioCategory.SFX);
            }
        }

    }

    protected onAllPlantersPlanted() {
        this.idleTimeline?.kill();
        this.idleTimeline?.clear();
        this.homeButton.visible = false;
        this.trackEvent("gameComponentComplete", {
            componentType: "Simulation",
            componentName: "Plant",
            duration: (new Date().valueOf() - this.screenStartTime) / 1000,
        });
        this.trackEvent("gameRoundComplete", {
            roundNumber: Application.instance.round,
            duration: (new Date().valueOf() - PlanterGameModel.GameState.roundStartTime) / 1000,
        });
        if (LightsOutGameModel.round > Application.instance.playthrough && PlanterGameModel.GameState.round === Application.instance.playthrough && TrashGameModel.round > Application.instance.playthrough) {
            this.trackEvent("gamePlaythroughComplete", {
                duration: (new Date().valueOf() - Application.instance.playthroughStartTime) / 1000,
                playthroughNumber: Application.instance.playthrough,
            });
            Application.instance.playthrough++;
            Application.instance.playthroughStartTime = new Date().valueOf();
        }
        Application.instance.round++;
        PlanterGameModel.GameState.round++;

        new TimelineLite()
            .to(this.buttonsContainer, 3, {pixi: {y: this._size.y / 2 }});
        HLF.Application.instance.voiceover.playVO(random([VO.PP_PLANTING09, VO.PP_PLANTING10]), () => {
            TweenLite.delayedCall(6.5, () => {
                this.abby.visible = false;
                this.trackEvent("gameAchievement", {achievementType: "Planter Party Badge", roundNumber: (Application.instance.round - 1)});
                this._subscriptions.push(PubSub.subscribe("home button clicked", () => this.trackEvent("gameMenuClick", {buttonName: "Home"})));
                this._subscriptions.push(PubSub.subscribe("home button auto clicked", () => this.trackEvent("gameMenuClick", {buttonName: "AutoHome"})));
                this._subscriptions.push(PubSub.subscribe("blackout clicked", () => this.trackEvent("gameMenuClick", {buttonName: "NotHome"})));
                PubSub.publishSync(HLF.SHOW_POPUP, new HLF.PopupToken(PlantingBadgePopup.NAME, this.onBadgePopupClosed.bind(this), true, false));
            });
        });

    }

    protected Plant(pPlant: PlantButton, pPlanter: Planter) {
        this.abby.Wand();
        const wandBone = this.abby.spine.skeleton.findBone("right-hand");
        const wandPos = this.abby.toGlobal({x: wandBone.worldX, y: wandBone.worldY});
        const particlePos = this.wandParticlesContainer.toLocal(wandPos);
        particlePos.y -= 100;
        particlePos.x += 50;
        this.wandParticles.spawnPos.copyFrom(particlePos);
        this.wandParticles.emit = true;

        this.trackEvent("gameInteraction", {objectName: pPlant.name.replace(/^\w/, (c) => c.toUpperCase())});
        this.trackEvent("gameQuestionCA", {
            questionAsked: pPlanter.name.replace(/^\w/, (c) => c.toUpperCase()),
            questionNumber: this.clickCount,
            playerAnswer: pPlant.name.replace(/^\w/, (c) => c.toUpperCase()),
        });

        const plantToPlant = pPlant.plantName;
        const planterDefaultScale = pPlanter === this.customizedPlanter ? 0.25 : 0.2;

        new TimelineLite()
                // scale down planter
                .to(pPlanter, 0.5, {pixi: {
                    scale: planterDefaultScale,
                    x: this.planterPreviousPosition.x,
                    y: this.planterPreviousPosition.y,
                }})
                // plant flower
                .call(() => {
                    pPlanter.Plant(plantToPlant);
                    pPlanter.hoverVO = pPlant.nameVO;
                })
                .call(() => {
                    particlePos.x = this.planterPreviousPosition.x;
                    particlePos.y = this.planterPreviousPosition.y - 100;
                    this.wandParticles.spawnPos.copyFrom(particlePos);

                    if (this.clickCount % 3 === 0) {
                        HLF.Application.instance.voiceover.playVO(plantReactVO(), HLF.PlayMode.Append, () => {
                            this.idleTimeline?.restart(true);
                        });
                    }

                    // reuse bee once all visible
                    const nextPollinator = this.pollinators.filter((bug) => !bug.visible).shift() ?? this.bee;
                    if (!nextPollinator.visible) {
                        nextPollinator.alpha = 0;
                        nextPollinator.visible = true;
                        this.makeHover(nextPollinator);
                    }
                    // flip if going left
                    if (nextPollinator.x > pPlanter.x) {
                        nextPollinator.scale.x = Math.abs(nextPollinator.scale.x) * -1;
                    }
                    else {
                        nextPollinator.scale.x = Math.abs(nextPollinator.scale.x);
                    }
                    new TimelineLite().to(nextPollinator, 1.5, {
                        pixi: {
                            alpha: 1,
                            x: pPlanter.x,
                        },
                    }).call(() => {
                        this.wandParticles.emit = false;
                    });
                    if (nextPollinator instanceof PlanterPartyEasterEgg/*  && nextPollinator.turnedOff */) {
                        nextPollinator.TurnOn();
                    }

                    // check if all planters now have flowers
                    if (![this.customizedPlanter, ...this.planter].find((p) => !p.planted)) {
                        this.onAllPlantersPlanted();
                    }
                    else {
                        // scale up next planter
                        for (let i = 0; i < this.planter.length; i++) {
                            const nextPlanter = this.planter[i];
                            if (!nextPlanter.planted) {
                                sendToFront(nextPlanter);
                                const planterHightlightedScale = nextPlanter === this.customizedPlanter ? 3 : 1;

                                this.planterPreviousPosition = nextPlanter.position.clone();
                                new TimelineLite()
                                    .to(nextPlanter, 0.5, {pixi: {
                                        scale: planterHightlightedScale,
                                        x: this.planterHighlightedPosition.x,
                                        y: this.planterHighlightedPosition.y,
                                    }})
                                    .call(() => {
                                        this.selectedPlanter = nextPlanter;
                                        this.button.forEach((b) => b.pulse());
                                        Application.instance.voiceover.playVO(VO.PP_PLANTING01B, HLF.PlayMode.Append, () => {
                                            this.button.forEach((b) => b.stopPulse());
                                            this.idleTimeline?.restart(true);
                                        });
                                    })
                                    .delay(3)
                                ;
                                break;
                            }
                        }
                    }
                })
            ;
    }

    private makeHover(pTarget: Button) {
        const s = 50; // radius of figure 8
        const d = 1; // speed in seconds to do a quarter of the animation

        const hoverTimeline = new TimelineMax();
        hoverTimeline.to(pTarget.sprite.position, d, {
            bezier: {
                type: "thru",
                values: [
                    { x: s / 2, y: s / 2 },
                    { x: s, y: 0 },
                ],
            },
            ease: Linear.easeNone,
        })
            .call(() => {
                pTarget.sprite.scale.x *= -1;
            })
            .to(pTarget.sprite.position, d, {
                bezier: {
                    type: "thru",
                    values: [
                        { x: s / 2, y: -s / 2 },
                        { x: 0, y: 0 },
                    ],
                },
                ease: Linear.easeNone,
            })
            .to(pTarget.sprite.position, d, {
                bezier: {
                    type: "thru",
                    values: [
                        { x: -s / 2, y: s / 2 },
                        { x: -s, y: 0 },
                    ],
                },
                ease: Linear.easeNone,
            })
            .call(() => {
                pTarget.sprite.scale.x *= -1;
            })
            .to(pTarget.sprite.position, d, {
                bezier: {
                    type: "thru",
                    values: [
                        { x: -s / 2, y: -s / 2 },
                        { x: 0, y: 0 },
                    ],
                },
                ease: Linear.easeNone,
            });

        hoverTimeline.repeat(-1);

        this.hoverTimelines.push(hoverTimeline);
    }
}
